import React, { useEffect, useMemo, useState } from "react";
import { DropdownChevronDown } from "@vibe/icons";
import { motion } from "motion/react";
import ItemNameComponent from "./ItemNameComponent";
import ExpandedRow from "./ExpandedRow";
import { Skeleton, Text } from "@vibe/core";
import DiscrepancyListSkeleton from "./DiscrepancyListSkeleton";
import { ALARM_CHIP, CLEAN_CHIP, OPEN_ROW_GROUND } from "./discrepancyChips";
import type { ApplyOptimisticUpdate } from "./resynchronization.interface";
import { resolveItemDisplayName } from "./displayName";

interface Item {
  itemid: string;
  boardid: string;
}

interface SyncedItem {
  _id: string;
  items: Item[];
  item_name?: string;
}

interface ItemsTableProps {
  syncedItems: SyncedItem[];
  filterQuery: string;
  discrepancies: any[];
  enableDropdown: (value: boolean) => void;
  handleFilteredTotal: (value: number) => void;
  startIndex: number;
  limit: number;
  itemsLoading: boolean;
  isProcessing: boolean;
  isValidating: boolean;
  applyOptimisticUpdate: ApplyOptimisticUpdate;
  fetchSyncItemValues: (syncId: string) => Promise<any>;
  /** The board this page is viewed from — which mirror's live name a row shows. */
  currentBoard?: string | number | null;
}

const DISCREPANCIES_FILTER = "discrepancies";

// Alphabetical comparator for synced-item names. Discrepancies mode sorts the full
// accumulated set with this BEFORE paginating (the only place it can be globally
// ordered, since chunks stream in independently); All mode is already globally
// ordered by the backend, so this keeps each page internally consistent.
const compareItemNames = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a === "") return 1;
  if (b === "") return -1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

const ItemsTable: React.FC<ItemsTableProps> = ({
  syncedItems,
  filterQuery,
  discrepancies,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  enableDropdown,
  handleFilteredTotal,
  startIndex,
  limit,
  itemsLoading,
  isProcessing,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isValidating,
  applyOptimisticUpdate,
  fetchSyncItemValues,
  currentBoard,
}) => {
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  const rowDataBySyncId = useMemo(() => {
    const map = new Map<string, any>();
    (discrepancies ?? []).forEach((row: any) => map.set(row.syncId, row));
    return map;
  }, [discrepancies]);

  // In Discrepancies mode only rows with discrepancies are shown; in All mode every
  // synced item is shown immediately — badges fill in as results stream.
  // syncedItems is already in stable scan-completion order, so paging never reshuffles earlier pages.
  const visibleSyncItems = useMemo(() => {
    if (filterQuery === DISCREPANCIES_FILTER) {
      return syncedItems.filter(
        (syncItem) => (rowDataBySyncId.get(syncItem._id)?.total_discrepancies ?? 0) > 0,
      );
    }
    // All mode: show every page row; badges fill in as results stream.
    return syncedItems;
  }, [syncedItems, rowDataBySyncId, filterQuery]);

  // Report the filtered count so the parent can size pagination for Discrepancies mode.
  useEffect(() => {
    handleFilteredTotal(visibleSyncItems.length);
  }, [visibleSyncItems.length, handleFilteredTotal]);

  // Order rows alphabetically by their displayed item name. In Discrepancies mode this
  // runs over the whole accumulated set, so it must precede the page slice below.
  // All-mode rows may have no verdict yet, so fall back to the synced item's own name.
  // Same resolver as the row label below, so a row sorts by the name it shows.
  const sortedSyncItems = useMemo(() => {
    const nameOf = (syncItem: any) =>
      resolveItemDisplayName({
        rowData: rowDataBySyncId.get(syncItem._id),
        syncItem,
        currentBoard,
      });
    return [...visibleSyncItems].sort((a, b) => compareItemNames(nameOf(a), nameOf(b)));
  }, [visibleSyncItems, rowDataBySyncId, currentBoard]);

  // All mode: backend already returns this page globally ordered. Discrepancies mode:
  // client-paginate the sorted accumulated set with the startIndex/limit the parent passes.
  const pageItems =
    filterQuery === DISCREPANCIES_FILTER
      ? sortedSyncItems.slice(startIndex, startIndex + limit)
      : sortedSyncItems;

  const handleToggleOpen = (syncId: string) => {
    setOpenItems((prev) => ({ ...prev, [syncId]: !prev[syncId] }));
  };

  const hasVisibleDiscrepancyItems = visibleSyncItems.length > 0;

  const shouldShowSkeletonRows =
    itemsLoading ||
    (filterQuery === DISCREPANCIES_FILTER && isProcessing && !hasVisibleDiscrepancyItems);

  const shouldShowEmptyMessage =
    filterQuery === DISCREPANCIES_FILTER
      ? !itemsLoading && !isProcessing && !hasVisibleDiscrepancyItems
      : !itemsLoading && syncedItems.length === 0;

  if (shouldShowEmptyMessage) {
    return (
      <div className="text-center flex items-center justify-center text-light-secondary-text-color h-10">
        No items to display.
      </div>
    );
  }

  return (
    <div>
      {/* One bordered container around the whole set, neighbours divided by a single hairline
          each — rather than a card per item separated by gutters. Matches the Subitems tab, so
          the two lists read as one product. */}
      {/* Border set inline, not via Tailwind's `border` utility: this project disables
          Preflight (tailwind.config.js `corePlugins.preflight: false`), and Preflight is what
          sets `border-style: solid` on `*`. Without it `border` compiles to width and colour
          only, leaving border-style at its initial `none` — a rule that renders nothing.
          The `solid` in this shorthand is the part Tailwind cannot supply here. */}
      <div
        className="mt-1"
        style={{
          border: "1px solid var(--layout-border-color)",
          borderRadius: "var(--border-radius-medium)",
          overflow: "hidden",
          background: "var(--primary-background-color)",
        }}
      >
        {shouldShowSkeletonRows ? (
          /* `limit`, not a literal: the skeleton stands in for a full page, so a hardcoded
             count agrees with reality only while the page size happens to match it. At limit
             25 a fixed 8 collapsed the loading state to a third of the height the rows would
             occupy, and the list jumped when they landed.

             The rows themselves moved to DiscrepancyListSkeleton, shared with the Subitems
             tab — which had no loading state at all and rendered an empty bordered box while
             its scan ran. */
          <DiscrepancyListSkeleton rows={limit} />
        ) : (
          pageItems.map((syncItem, rowIndex) => {
            const rowData = rowDataBySyncId.get(syncItem._id);
            // Discrepancies mode only ever lists rows that have a verdict; All mode
            // shows every row, so a missing verdict is expected (still streaming).
            if (filterQuery === DISCREPANCIES_FILTER && !rowData) return null;
            const isOpen = openItems[syncItem._id] || false;
            const displayName = resolveItemDisplayName({ rowData, syncItem, currentBoard });
            const hasVerdict = Boolean(rowData);

            return (
              <div
                key={syncItem._id}
                style={{
                  borderTop:
                    rowIndex === 0 ? "none" : "1px solid var(--layout-border-color)",
                }}
              >
                <div className="overflow-hidden bg-[var(--primary-background-color)]">
                  <div
                    onClick={() => handleToggleOpen(syncItem._id)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleToggleOpen(syncItem._id);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 min-h-10 cursor-pointer"
                    style={{
                      background: isOpen ? OPEN_ROW_GROUND : "var(--primary-background-color)",
                      borderBottom: isOpen ? "1px solid var(--layout-border-color)" : "none",
                    }}
                  >
                    {/* Leads the row, as on the Subitems tab — on a row this wide the toggle
                        was a long way from the name it opens. */}
                    <motion.span
                      className="inline-flex flex-none text-[var(--secondary-text-color)]"
                      animate={{ rotate: isOpen ? 0 : -90 }}
                      transition={{ duration: 0.15 }}
                    >
                      <DropdownChevronDown size={20} />
                    </motion.span>
                    <div className="flex flex-row gap-1 items-center min-w-0">
                      <ItemNameComponent itemName={displayName} />
                      {hasVerdict ? (
                        rowData.status === "unverified" ? (
                          // Wording matches SubitemsTable exactly — the two tabs describe the
                          // same condition, and a reader should not have to learn it twice.
                          <Text type="text2" color="secondary">
                            Scan incomplete
                          </Text>
                        ) : rowData.total_discrepancies > 0 ? (
                          <span style={ALARM_CHIP}>{`${rowData.total_discrepancies} issues`}</span>
                        ) : (
                          // Previously rendered nothing at all, so "we checked and this is
                          // fine" looked identical to a row still streaming its verdict — and
                          // the skeleton below means this list genuinely has both states.
                          <span
                            style={CLEAN_CHIP}
                            data-testid={`items-table-clean-${syncItem._id}`}
                          >
                            In sync
                          </span>
                        )
                      ) : (
                        <Skeleton
                          data-testid={`badge-loading-${syncItem._id}`}
                          width={16}
                          height={16}
                        />
                      )}
                    </div>
                  </div>
                  <ExpandedRow
                    isOpen={isOpen}
                    syncId={syncItem._id}
                    dbItems={syncItem.items}
                    rowData={rowData}
                    filterQuery={filterQuery}
                    applyOptimisticUpdate={applyOptimisticUpdate}
                    fetchSyncItemValues={fetchSyncItemValues}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ItemsTable;
